import type { ApiErrorResponse, AxiosErrorResponse, ErrorResponse } from "types/error.types";

/**
 * Helper function to extract message from string or array
 */
function extractMessage(value: string | string[] | undefined | null): string | null {
	if (!value) return null;

	if (typeof value === "string") {
		return value.trim() || null;
	}

	if (Array.isArray(value) && value.length > 0) {
		const firstMessage = value[0];
		return typeof firstMessage === "string" ? firstMessage.trim() || null : null;
	}

	return null;
}

function isAxiosStatusMessage(message: string): boolean {
	return /^Request failed with status code \d+$/.test(message.trim());
}

function extractApiErrorMessage(errorObj: ApiErrorResponse & AxiosErrorResponse): string | null {
	const possibleMessages: (string | null)[] = [
		extractMessage(errorObj.response?.data?.message),
		extractMessage(errorObj.response?.data?.error),
		extractMessage(errorObj.response?.data?.errors),
		extractMessage(errorObj.data?.message),
		extractMessage(errorObj.data?.error),
		extractMessage(errorObj.data?.errors),
		extractMessage(errorObj.message),
		extractMessage(errorObj.error),
		extractMessage(errorObj.errors),
		errorObj.response?.statusText ?? errorObj.statusText ?? null,
	];

	for (const message of possibleMessages) {
		if (message && message.length > 0 && !isAxiosStatusMessage(message)) {
			return message;
		}
	}

	return null;
}

/**
 * Extracts error message from various API error response structures
 * @param error - The error object from API response
 * @param fallbackMessage - Default message if no error message found
 * @returns Formatted error message string
 */
export function getErrorMessage(
	error: ErrorResponse,
	fallbackMessage: string = "Something went wrong from backend",
): string {
	// If error is already a string, return it
	if (typeof error === "string") {
		return error.trim() || fallbackMessage;
	}

	// If error is null or undefined
	if (!error) {
		return fallbackMessage;
	}

	// AxiosError extends Error — prefer API body over Axios' generic status message
	if (error instanceof Error) {
		const apiMessage = extractApiErrorMessage(error as ApiErrorResponse & AxiosErrorResponse);
		if (apiMessage) {
			return apiMessage;
		}

		if (error.message && !isAxiosStatusMessage(error.message)) {
			return error.message;
		}

		return fallbackMessage;
	}

	const apiMessage = extractApiErrorMessage(error as ApiErrorResponse & AxiosErrorResponse);
	if (apiMessage) {
		return apiMessage;
	}

	return fallbackMessage;
}

/**
 * Enhanced error handler specifically for React Query mutations
 * @param error - The error object from mutation
 * @param customFallback - Custom fallback message (optional)
 * @returns Formatted error message
 */
export function handleMutationError(error: ErrorResponse, customFallback: string): string {
	return getErrorMessage(error, customFallback);
}

export interface AuthFormErrorMessages {
	generic: string;
	server: string;
	network: string;
}

function isNetworkAxiosError(error: ErrorResponse): boolean {
	if (!error || typeof error !== "object") {
		return false;
	}

	const errorObj = error as AxiosErrorResponse;
	return Boolean(errorObj.request) && !errorObj.response;
}

function pickAuthFormErrorFallback(error: ErrorResponse, messages: AuthFormErrorMessages): string {
	if (!error) {
		return messages.network;
	}

	if (isNetworkAxiosError(error)) {
		return messages.network;
	}

	const statusCode = getErrorStatusCode(error);
	if (statusCode === 500) {
		return messages.server;
	}

	return messages.generic;
}

/**
 * Resolves the message shown in auth form root error banners.
 * Prefers API/server messages from the full mutation error (Axios or client Error).
 */
export function resolveAuthFormErrorMessage(error: unknown, messages: AuthFormErrorMessages): string {
	const errorResponse = error as ErrorResponse;
	const fallback = pickAuthFormErrorFallback(errorResponse, messages);
	return handleMutationError(errorResponse, fallback);
}

/**
 * Type guard to check if error has expected API structure
 * @param error - Error object to check
 * @returns boolean indicating if error has expected API structure
 */
export function isApiError(error: unknown): error is ApiErrorResponse {
	return (
		error !== null &&
		error !== undefined &&
		typeof error === "object" &&
		("data" in error || "message" in error || "response" in error)
	);
}

/**
 * Type guard to check if error is an Axios error
 * @param error - Error object to check
 * @returns boolean indicating if error is an Axios error
 */
export function isAxiosError(error: unknown): error is AxiosErrorResponse {
	return error !== null && error !== undefined && typeof error === "object" && "response" in error;
}

/**
 * Extracts status code from error response
 * @param error - The error object
 * @returns HTTP status code or null
 */
export function getErrorStatusCode(error: ErrorResponse): number | null {
	if (!error || typeof error !== "object") {
		return null;
	}

	const errorObj = error as ApiErrorResponse & AxiosErrorResponse;

	return errorObj.response?.status || errorObj.status || errorObj.statusCode || null;
}
